import React, { useEffect, useState } from "react";
import BlogPost from "crm/other/BlogPost";
import IndustryDetailTemplate from "pages/industries/IndustryDetailTemplate";
import { getBlogsBySlug } from "helpers/api/BlogService";
import { useParams, useLocation } from "react-router-dom";
import NotFound from "./NotFound";
import { Helmet } from "react-helmet-async";
import config from "config";

const menuRoutes = [
    "features",
    "industries",
    "about",
    "contact",
    "blogs",
    "privacy",
    "termsAndConditions",
];

export default function DynamicPage() {
    const { slug } = useParams<{ slug: string }>();
    const location = useLocation();

    const stateData = location.state as {
        type?: "blog" | "industry";
        data?: any;
    } | null;

    const [type, setType] = useState<"blog" | "industry" | "notfound">("blog");
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    console.log("DynamicPage - Slug:", slug, "| Type:", type, "| Has data:", !!data);

    useEffect(() => {
        console.log("Effect triggered - Processing slug:", slug);

        setNotFound(false);
        setData(null);

        if (!slug) {
            console.log("No slug provided");
            setType("notfound");
            setNotFound(true);
            setLoading(false);
            return;
        }

        if (menuRoutes.includes(slug)) {
            console.log("Slug matches menu route:", slug);
            setType("notfound");
            setNotFound(true);
            setLoading(false);
            return;
        }

        let isMounted = true;

        const fetchBlogData = async () => {
            try {
                if (stateData?.type && stateData?.data) {
                    console.log("STEP 1: Immediate load from state data");
                    setType(stateData.type);
                    setData(stateData.data);
                    setNotFound(false);
                    setLoading(false);

                    try {
                        const res = await getBlogsBySlug(slug);
                        if (isMounted && res?.success && res.result) {
                            console.log("STEP 2: Background API call completed - updating with full data");
                            setData(res.result);
                            setType(res.result.isBlog === false ? "industry" : "blog");
                            setNotFound(false);
                        }
                    } catch (apiError) {
                        console.log("STEP 2: Background API failed - keeping state data");
                    }
                    return;
                }

                console.log("STEP 3: Direct URL access - Fetching from API with slug:", slug);
                setLoading(true);
                const res = await getBlogsBySlug(slug);

                if (isMounted) {
                    if (res?.success && res.result) {
                        console.log("STEP 4: API returned data - Setting:", res.result.isBlog ? "Blog" : "Industry");
                        setData(res.result);
                        setType(res.result.isBlog === false ? "industry" : "blog");
                        setNotFound(false);
                    } else {
                        console.log("STEP 4: API returned no data");
                        setType("notfound");
                        setNotFound(true);
                    }
                    setLoading(false);
                }
            } catch (error) {
                console.error("ERROR fetching data:", error);
                if (isMounted) {
                    setType("notfound");
                    setNotFound(true);
                    setLoading(false);
                }
            }
        };

        fetchBlogData();

        return () => {
            isMounted = false;
        };
    }, [slug, stateData?.type, stateData?.data]);

    if (loading) {
        return (
            <>
                <Helmet>
                    <title>Loading Content | Sales Trackify</title>
                    <meta name="robots" content="index, follow" />
                    <link rel="canonical" href={`${config.DOMAIN_URL}/${slug}`} />
                </Helmet>
                <div className="d-flex align-items-center justify-content-center" style={{ minHeight: "100vh" }}>
                    <div className="text-center">
                        <div className="spinner-border mb-3" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p>Loading content...</p>
                    </div>
                </div>
            </>
        );
    }

    if (data) {
        if (type === "industry" || data.isBlog === false) {
            return <IndustryDetailTemplate blogData={data} />;
        }
        return <BlogPost blogData={data} />;
    }

    if (notFound) {
        return (
            <>
                <Helmet>
                    <title>404 - Page Not Found | Sales Trackify</title>
                    <meta name="robots" content="noindex, nofollow" />
                    <link rel="canonical" href={`${config.DOMAIN_URL}/${slug}`} />
                </Helmet>
                <NotFound />
            </>
        );
    }

    return null;
}
